import { partition } from "lodash-es";
import { FC, useState } from "react";

import { AddSectionModal } from "../AddSectionModal";

import { LoadingScreen } from "components/LoadingScreen";
import { EditableField } from "components/hover-widgets/EditableField";
import { AdminSectionListPanel } from "components/materials/home/admin/content/AdminSectionList";
import { CourseOptionsMenu } from "components/menus/CourseOptionsMenu";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { AddPlusIcon, ChevronRightIcon, FolderIcon, TextBooksIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { courseGroupingSelectors, selectSortedTermSectionsByCourse } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { onCodeChanged } from "utils/store-utils";

type SectionDetailTermViewProps = {
  course: CourseType;
  eventPrefix?: string;
  canEdit: boolean;
};

export const SectionDetailTermView: FC<SectionDetailTermViewProps> = ({
  course,
  eventPrefix,
  canEdit,
}) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const termSections = useListSelector((s) => selectSortedTermSectionsByCourse(s, course?.id));
  const visibleTermSections = termSections.filter((term) => term.sections.length > 0);
  const [archivedTermSections, activeTermSections] = partition(
    visibleTermSections,
    (term) => term.archived_at,
  );
  const grouping = useAppSelector((s) => courseGroupingSelectors.selectById(s, course.grouping_id));

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  if (!course) {
    return <LoadingScreen>{t("home.settings.no_sections")}</LoadingScreen>;
  }

  const showAddSectionModal = () => {
    showConfirmationModal({
      title: t("home.add_section_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddSectionModal
          defaultCourseId={course.id}
          eventName={eventPrefix ? `${eventPrefix}Add Course` : undefined}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div className="px-0 py-3 md:px-7">
      <h2 className="mb-3 flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {grouping && (
            <div className="flex items-center gap-2">
              <FolderIcon className="mb-[3px] text-blue" size="l" />
              <span className="text-black-tint-20">{grouping.title}</span>
              <ChevronRightIcon />
            </div>
          )}

          <div className="flex items-center gap-2">
            <TextBooksIcon className="mt-[1px] text-blue" size="l" />

            <EditableField
              isEditing={isEditing}
              label={t("glossary.course_title")}
              textSize="h2"
              value={course.code}
              onSetIsEditing={setIsEditing}
              onValueChanged={(newCode) => onCodeChanged("courses", course.id, newCode)}
            />
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <CourseOptionsMenu
              course={course}
              eventPrefix={eventPrefix}
              kind="gray"
              onRename={() => setIsEditing(true)}
            />

            <Button
              eventName={eventPrefix ? `${eventPrefix}Add Section` : undefined}
              kind="gray"
              size="s"
              onClick={showAddSectionModal}
            >
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
            </Button>
          </div>
        )}
      </h2>

      {!visibleTermSections.length ? (
        <Banner
          buttonChildren={
            canEdit ? (
              <Button
                className="px-2"
                eventName={eventPrefix ? `${eventPrefix}Add Section` : undefined}
                kind="blue"
                size="s"
                onClick={showAddSectionModal}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
              </Button>
            ) : (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Enable Edit Mode` : undefined}
                kind="blue"
                size="s"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </Button>
            )
          }
          kind="info"
        >
          {canEdit
            ? t("home.courses.empty_course_banner_text")
            : t("home.courses.empty_course_banner_text_no_edit")}
        </Banner>
      ) : (
        <>
          <ul>
            {activeTermSections.map((term) => (
              <li className="course-panel ml-2" key={term.id}>
                <AdminSectionListPanel
                  canEdit={canEdit}
                  eventPrefix={eventPrefix}
                  eventProperties={{ course: course.code, courseTerm: term.title }}
                  sections={term.sections}
                  title={term.title}
                />
              </li>
            ))}
          </ul>

          {archivedTermSections.length > 0 && (
            <AccordionTitle
              headerClassName="ml-2 w-[calc(100%-12px)] justify-between border-b border-black-tint-90 pr-4"
              iconDirection="end"
              title={t("home.terms.archived_terms")}
            >
              <ul>
                {archivedTermSections.map((term) => (
                  <li className="course-panel ml-2" key={term.id}>
                    <AdminSectionListPanel
                      canEdit={canEdit}
                      eventPrefix={eventPrefix}
                      eventProperties={{ course: course.code, courseTerm: term.title }}
                      sections={term.sections}
                      title={term.title}
                      isDisabled
                    />
                  </li>
                ))}
              </ul>
            </AccordionTitle>
          )}
        </>
      )}
    </div>
  );
};
